/**
 * جدول روی `@tanstack/react-table`.
 *
 * دلیل استفاده از کتابخانه: مرتب‌سازی، انتخاب ردیف و صفحه‌بندی سه چیزی هستند
 * که پیاده‌سازی دستی‌شان همیشه با یک حالت لبه می‌شکند (مرتب‌سازی پایدار، ردیف
 * انتخاب‌شده پس از فیلتر، شماره‌ی صفحه پس از حذف). این کتابخانه headless است،
 * پس ظاهر همچنان مال ماست.
 */

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/lib/cn'

interface DataTableProps<TData> {
  columns: ColumnDef<TData, string>[]
  data: TData[]
  /** ردیف کلیک‌شدنی. در نبودش، ردیف‌ها فقط خواندنی‌اند. */
  onRowClick?: (row: TData) => void
  /** شناسه‌ی ردیف انتخاب‌شده، برای برجسته‌سازی. */
  selectedId?: string
  getRowId?: (row: TData) => string
  emptyMessage?: string
}

export const DataTable = <TData,>({
  columns,
  data,
  onRowClick,
  selectedId,
  getRowId,
  emptyMessage = 'موردی برای نمایش نیست.',
}: DataTableProps<TData>) => {
  const [sorting, setSorting] = useState<SortingState>([])

  /*
   * React Compiler برای این کامپوننت memoization را رد می‌کند، چون
   * `useReactTable` توابعی برمی‌گرداند که memoize کردنشان می‌تواند UI کهنه
   * بسازد. این یک هشدار اطلاعی است، نه اشکال: نتیجه‌اش فقط این است که این
   * کامپوننت بهینه‌سازی خودکار نمی‌گیرد. جدول‌های این رابط ده‌ها ردیف دارند،
   * نه هزاران، پس هزینه‌ای ندارد.
   */
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(getRowId === undefined ? {} : { getRowId }),
  })

  if (data.length === 0) {
    return <p className="px-4 py-10 text-center text-sm text-ink-muted">{emptyMessage}</p>
  }

  return (
    <div className="scrollbar-thin overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-sm">
        {/* سر جدول چسبان و شیشه‌ای است: در فهرست بلند، نام ستون‌ها باید بمانند. */}
        <thead className="glass sticky top-14 z-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort()
                const direction = header.column.getIsSorted()
                return (
                  <th
                    key={header.id}
                    className="border-b border-line px-4 py-3 text-start text-[11px] font-semibold tracking-wide whitespace-nowrap text-ink-muted"
                    aria-sort={
                      direction === 'asc'
                        ? 'ascending'
                        : direction === 'desc'
                          ? 'descending'
                          : undefined
                    }
                  >
                    {canSort ? (
                      <button
                        type="button"
                        className="group inline-flex items-center gap-1.5 rounded-sm transition-colors hover:text-accent"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {direction === 'asc' ? (
                          <ArrowUp className="size-3" aria-hidden />
                        ) : direction === 'desc' ? (
                          <ArrowDown className="size-3" aria-hidden />
                        ) : (
                          <ChevronsUpDown className="size-3 opacity-40" aria-hidden />
                        )}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const isSelected = selectedId !== undefined && row.id === selectedId
            return (
              <tr
                key={row.id}
                className={cn(
                  'group transition-colors',
                  isSelected && 'bg-accent-soft/70',
                  onRowClick !== undefined && 'cursor-pointer hover:bg-accent-soft/40',
                )}
                {...(onRowClick === undefined
                  ? {}
                  : {
                      onClick: () => {
                        onRowClick(row.original)
                      },
                      // ردیف کلیک‌شدنی باید با کیبورد هم قابل فعال‌سازی باشد،
                      // وگرنه کاربر کیبورد به جزئیات نمی‌رسد.
                      tabIndex: 0,
                      role: 'button',
                      onKeyDown: (event: React.KeyboardEvent) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onRowClick(row.original)
                        }
                      },
                    })}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      'border-b border-line/70 px-4 py-3 align-top text-ink',
                      // ردیف انتخاب‌شده یک میله‌ی رنگی در ابتدای سطر می‌گیرد.
                      isSelected &&
                        'first:relative first:before:absolute first:before:inset-y-0 first:before:start-0 first:before:w-0.5 first:before:bg-linear-to-b first:before:from-accent first:before:to-accent-two',
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
